import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, User, Mail, Phone, Camera, CreditCard, Shield, ShieldCheck as ShieldIcon, LogOut, Trash2, Save, Crown, ExternalLink, AlertTriangle, Wallet, Building2, Zap, CheckCircle2, Video, Lock } from 'lucide-react';
import ProviderVerification from '../components/ProviderVerification';
import MicrositeCustomizer from '../components/MicrositeCustomizer';
import ChangePasswordCard from '../components/ChangePasswordCard';
import IntroVideoManager from '../components/IntroVideoManager';
import StripeConnectCard from '../components/StripeConnectCard';
import MyStoreCard from '../components/MyStoreCard';
import PrivacyCenter from '../components/PrivacyCenter';
import AppShell from '../components/AppShell';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfileSettings() {
  const { user, token, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Honor ?tab=<id> in the URL so the Founder onboarding checklist and
  // other in-app deep-links can drop the user straight onto the right pane.
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [wecall, setWecall] = useState({ webhook_url: '', webhook_key: '', auto_push: true, configured: false });
  const [wecallSaving, setWecallSaving] = useState(false);
  const [wecallTesting, setWecallTesting] = useState(false);
  const [wecallResult, setWecallResult] = useState(null);
  const [wecallDeliveries, setWecallDeliveries] = useState([]);
  
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phone: '',
    bio: '',
    display_name: '',
    specialty: '',
    profile_photo: '',
    contact_email: '',
    contact_phone: '',
    lead_notification_webhook: ''
  });

  const [payoutHistory, setPayoutHistory] = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchProfile();
  }, [user, authLoading]);

  useEffect(() => {
    if (activeTab === 'integrations' && token) fetchWecallDeliveries();
  }, [activeTab, token]);

  const fetchProfile = async () => {
    try {
      const [profileRes, historyRes, wecallRes] = await Promise.all([
        axios.get(`${API_URL}/api/users/profile`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/users/payout-history`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/integrations/wecall/me`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { webhook_url: '', webhook_key: '', auto_push: true, configured: false } })),
      ]);
      setProfile({
        name: profileRes.data.name || '',
        email: profileRes.data.email || '',
        phone: profileRes.data.phone || '',
        bio: profileRes.data.bio || '',
        display_name: profileRes.data.display_name || '',
        specialty: profileRes.data.specialty || '',
        profile_photo: profileRes.data.profile_photo || '',
        contact_email: profileRes.data.contact_email || profileRes.data.email || '',
        contact_phone: profileRes.data.contact_phone || profileRes.data.phone || '',
        lead_notification_webhook: profileRes.data.lead_notification_webhook || ''
      });
      setPayoutHistory(Array.isArray(historyRes.data) ? historyRes.data : []);
      if (wecallRes.data) {
        setWecall({
          webhook_url: wecallRes.data.webhook_url || '',
          webhook_key: wecallRes.data.webhook_key || '',
          auto_push: wecallRes.data.auto_push !== false,
          configured: !!wecallRes.data.configured,
        });
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/users/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be less than 5MB');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/api/users/profile/photo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setProfile({ ...profile, profile_photo: response.data.url });
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const handleCancelMembership = async () => {
    try {
      await axios.post(`${API_URL}/api/memberships/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Membership cancellation requested');
      setShowCancelModal(false);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel membership');
    }
  };

  const handleManageBilling = async () => {
    try {
      toast.loading('Opening billing portal...');
      const response = await axios.post(`${API_URL}/api/stripe/billing-portal`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.dismiss();
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      toast.dismiss();
      const message = error.response?.data?.detail || 'Billing portal not available';
      if (message.includes('subscribe first')) {
        toast.error('No active subscription. Please upgrade your membership first.');
      } else {
        toast.error(message);
      }
    }
  };

  const handleSaveWecall = async (e) => {
    e.preventDefault();
    setWecallSaving(true);
    setWecallResult(null);
    try {
      const res = await axios.put(
        `${API_URL}/api/integrations/wecall/me`,
        { webhook_url: wecall.webhook_url, webhook_key: wecall.webhook_key, auto_push: wecall.auto_push },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWecall((w) => ({ ...w, configured: !!res.data?.configured, auto_push: res.data?.auto_push !== false }));
      toast.success('WECall AI credentials saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save WECall AI credentials');
    } finally {
      setWecallSaving(false);
    }
  };

  const handleTestWecall = async () => {
    setWecallTesting(true);
    setWecallResult(null);
    try {
      const res = await axios.post(
        `${API_URL}/api/integrations/wecall/me/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWecallResult(res.data);
      if (res.data?.ok) toast.success(`WECall AI responded ${res.data.status} OK`);
      else toast.error(`WECall AI responded ${res.data?.status || 'error'}`);
      fetchWecallDeliveries();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Test failed';
      setWecallResult({ ok: false, status: 0, error: detail });
      toast.error(detail);
    } finally {
      setWecallTesting(false);
    }
  };

  const fetchWecallDeliveries = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/integrations/wecall/me/deliveries?limit=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWecallDeliveries(res.data?.deliveries || []);
    } catch (_e) {
      // Silent — panel just stays empty.
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <AppShell active="profile" title="Profile Settings">
      <div className="border-b border-white/5 bg-brand-surface/50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-playfair font-semibold text-white">Profile Settings</h1>
          <Link to={`/member/${user?.id}`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm" className="border-brand-gold/40 text-brand-gold hover:bg-brand-gold/10">
              <ExternalLink className="w-4 h-4 mr-2" /> View Public Profile
            </Button>
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {[
            { id: 'profile', label: 'Profile', icon: User },
            { id: 'intro-video', label: 'Intro Video', icon: Video },
            { id: 'microsite', label: 'Micro-site', icon: Camera },
            { id: 'contact', label: 'Contact Settings', icon: Mail },
            { id: 'verification', label: 'Verification', icon: ShieldIcon },
            { id: 'payouts', label: 'Payouts', icon: Wallet },
            { id: 'integrations', label: 'Integrations', icon: Zap },
            { id: 'billing', label: 'Billing', icon: CreditCard },
            { id: 'privacy', label: 'Privacy', icon: Lock },
            { id: 'account', label: 'Account', icon: Shield }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon size={18} />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <form onSubmit={handleSave} className="space-y-6">
            <div className="wm-card p-6 rounded-lg">
              <h2 className="text-lg font-semibold text-foreground mb-4">Profile Information</h2>
              
              {/* Photo Upload */}
              <div className="flex items-center gap-6 mb-6">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full bg-muted overflow-hidden">
                    {profile.profile_photo ? (
                      <img src={profile.profile_photo} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-12 h-12 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <label className="absolute bottom-0 right-0 p-2 bg-primary rounded-full cursor-pointer hover:bg-primary/90">
                    <Camera className="w-4 h-4 text-primary-foreground" />
                    <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                  </label>
                </div>
                <div>
                  <p className="text-foreground font-medium">{user?.name}</p>
                  <p className="text-sm text-muted-foreground capitalize flex items-center gap-1">
                    {user?.is_founder && <Crown className="w-4 h-4 text-amber-400" />}
                    {user?.membership_tier || 'No membership'} Member
                  </p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-foreground">Full Name</Label>
                  <Input
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    className="bg-input text-foreground mt-1"
                  />
                </div>
                <div>
                  <Label className="text-foreground">Display Name (Public)</Label>
                  <Input
                    value={profile.display_name}
                    onChange={(e) => setProfile({ ...profile, display_name: e.target.value })}
                    placeholder="How you appear on your microsite"
                    className="bg-input text-foreground mt-1"
                  />
                </div>
                <div>
                  <Label className="text-foreground">Specialty / Title</Label>
                  <Input
                    value={profile.specialty}
                    onChange={(e) => setProfile({ ...profile, specialty: e.target.value })}
                    placeholder="e.g., Business Coach, Hair Stylist"
                    className="bg-input text-foreground mt-1"
                  />
                </div>
                <div>
                  <Label className="text-foreground">Phone</Label>
                  <Input
                    type="tel"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    className="bg-input text-foreground mt-1"
                  />
                </div>
              </div>

              <div className="mt-4">
                <Label className="text-foreground">Bio</Label>
                <Textarea
                  value={profile.bio}
                  onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                  placeholder="Tell visitors about yourself and your services..."
                  rows={4}
                  className="bg-input text-foreground mt-1"
                />
              </div>

              <Button type="submit" className="mt-6 bg-primary text-primary-foreground" disabled={saving}>
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Profile'}
              </Button>
            </div>
          </form>
        )}

        {/* Contact Settings Tab - Where "Get in Touch" leads go */}
        {activeTab === 'contact' && (
          <form onSubmit={handleSave} className="space-y-6">
            <div className="wm-card p-6 rounded-lg">
              <h2 className="text-lg font-semibold text-foreground mb-2">Contact Form Settings</h2>
              <p className="text-muted-foreground mb-6">
                Configure where leads from your &ldquo;Get in Touch&rdquo; form are sent. These appear in your CRM and notifications.
              </p>

              <div className="space-y-4">
                <div>
                  <Label className="text-foreground">Contact Email</Label>
                  <p className="text-xs text-muted-foreground mb-1">Leads will be sent to this email</p>
                  <Input
                    type="email"
                    value={profile.contact_email}
                    onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })}
                    placeholder="youremail@example.com"
                    className="bg-input text-foreground"
                  />
                </div>
                <div>
                  <Label className="text-foreground">Contact Phone</Label>
                  <p className="text-xs text-muted-foreground mb-1">Displayed on your public profile</p>
                  <Input
                    type="tel"
                    value={profile.contact_phone}
                    onChange={(e) => setProfile({ ...profile, contact_phone: e.target.value })}
                    placeholder="+1 (555) 123-4567"
                    className="bg-input text-foreground"
                  />
                </div>
                <div>
                  <Label className="text-foreground">Webhook URL (Optional)</Label>
                  <p className="text-xs text-muted-foreground mb-1">Receive real-time lead notifications via webhook (e.g., Zapier, Make)</p>
                  <Input
                    type="url"
                    value={profile.lead_notification_webhook}
                    onChange={(e) => setProfile({ ...profile, lead_notification_webhook: e.target.value })}
                    placeholder="https://hooks.zapier.com/..."
                    className="bg-input text-foreground"
                    data-testid="webhook-url-input"
                  />
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                <h4 className="font-medium text-blue-400 mb-2">How it works:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>When visitors fill out your &ldquo;Get in Touch&rdquo; form, they become a lead</li>
                  <li>Leads appear in your <Link to="/crm" className="text-primary hover:underline">CRM</Link> automatically</li>
                  <li>Set a webhook URL to get instant notifications (works with Zapier, Make, custom endpoints)</li>
                </ul>
              </div>

              <Button type="submit" className="mt-6 bg-primary text-primary-foreground" disabled={saving}>
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Contact Settings'}
              </Button>
            </div>
          </form>
        )}

        {/* Verification Tab */}
        {activeTab === 'verification' && (
          <div className="space-y-4">
            <ProviderVerification />
          </div>
        )}

        {/* Intro Video Tab — dedicated tab so it's discoverable (was previously
            buried inside the Micro-site tab). Members can record or upload
            a short intro that shows on their public profile + digital card. */}
        {activeTab === 'intro-video' && (
          <div className="space-y-4" data-testid="tab-intro-video">
            <IntroVideoManager />
          </div>
        )}

        {/* Micro-site Tab */}
        {activeTab === 'microsite' && (
          <div className="space-y-4">
            <MicrositeCustomizer />
          </div>
        )}

        {/* Payouts Tab */}
        {activeTab === 'payouts' && (
          <div className="space-y-6">
            {/* Stripe Connect onboarding — bank/identity/tax handled by Stripe */}
            <StripeConnectCard />

            {/* Sample storefront — sell products, paid straight to the member's Stripe account */}
            <MyStoreCard />

            {/* Payout History */}
            <div className="wm-card p-6 rounded-lg">
              <h2 className="text-lg font-semibold text-foreground mb-4">Payout History</h2>
              {payoutHistory.length === 0 ? (
                <p className="text-muted-foreground text-sm">No payouts yet. Commissions are paid out at the end of each month.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 text-muted-foreground">Date</th>
                        <th className="text-left py-2 text-muted-foreground">Amount</th>
                        <th className="text-left py-2 text-muted-foreground">Method</th>
                        <th className="text-left py-2 text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payoutHistory.map((p, i) => (
                        <tr key={i} className="border-b border-border/30">
                          <td className="py-2 text-foreground">{new Date(p.created_at).toLocaleDateString()}</td>
                          <td className="py-2 text-emerald-400 font-medium">${p.amount?.toFixed(2)}</td>
                          <td className="py-2 text-muted-foreground capitalize">{p.payout_type}</td>
                          <td className="py-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              p.status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                              p.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>{p.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Privacy Tab — what data we hold + deletion requests */}
        {activeTab === 'privacy' && (
          <PrivacyCenter onEditProfile={() => setActiveTab('profile')} />
        )}

        {/* Integrations Tab — per-member WECall AI credentials */}
        {activeTab === 'integrations' && (
          <form onSubmit={handleSaveWecall} className="space-y-6" data-testid="integrations-tab">
            <div className="wm-card p-6 rounded-lg">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-brand-gold/20 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-5 h-5 text-brand-gold" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    WECall AI
                    {wecall.configured && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Push your CRM leads to your own WECall AI account so it can call them using
                    the AI model you&apos;ve trained. Get your URL and key from
                    <span className="text-brand-gold"> WECall AI → Admin → Integration</span>.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="wecall-url" className="text-foreground">Webhook URL</Label>
                  <Input
                    id="wecall-url"
                    type="url"
                    value={wecall.webhook_url}
                    onChange={(e) => setWecall({ ...wecall, webhook_url: e.target.value })}
                    placeholder="https://directoryvoice.preview.emergentagent.com/api/webhook/leads"
                    className="mt-1 bg-input text-foreground"
                    data-testid="wecall-url-input"
                  />
                </div>
                <div>
                  <Label htmlFor="wecall-key" className="text-foreground">Webhook Key</Label>
                  <Input
                    id="wecall-key"
                    type="password"
                    value={wecall.webhook_key}
                    onChange={(e) => setWecall({ ...wecall, webhook_key: e.target.value })}
                    placeholder="Paste the secret key from WECall AI"
                    className="mt-1 bg-input text-foreground font-mono"
                    data-testid="wecall-key-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Sent as <code className="text-brand-gold">X-Webhook-Key</code> header on every push.
                  </p>
                </div>
              </div>

              {wecallResult && (
                <div className={`mt-4 rounded-lg p-3 text-sm ${
                  wecallResult.ok
                    ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-300'
                    : 'bg-red-500/10 border border-red-500/30 text-red-300'
                }`} data-testid="wecall-test-result">
                  {wecallResult.ok
                    ? <>✓ WECall AI accepted the test payload (HTTP {wecallResult.status}).</>
                    : <>✗ WECall AI rejected the test payload (HTTP {wecallResult.status || 0}). {wecallResult.error || wecallResult.body_preview}</>}
                </div>
              )}

              {/* Auto-push pause toggle */}
              <label className="flex items-start gap-3 mt-4 p-3 rounded-lg bg-black/30 border border-white/5 cursor-pointer" data-testid="wecall-autopush-toggle-wrap">
                <input
                  type="checkbox"
                  checked={!!wecall.auto_push}
                  onChange={(e) => setWecall({ ...wecall, auto_push: e.target.checked })}
                  className="mt-0.5 h-4 w-4 rounded border-border accent-brand-gold"
                  data-testid="wecall-autopush-toggle"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">
                    Auto-push new leads to WECall AI
                    {!wecall.auto_push && (
                      <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[10px] uppercase tracking-wider">Paused</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    When enabled, every new CRM lead is automatically sent to your WECall AI account. Uncheck to pause without clearing your credentials.
                  </p>
                </div>
              </label>

              <div className="flex flex-wrap gap-2 mt-6">
                <Button type="submit" disabled={wecallSaving} className="bg-primary text-primary-foreground" data-testid="wecall-save-btn">
                  <Save className="w-4 h-4 mr-2" /> {wecallSaving ? 'Saving…' : 'Save'}
                </Button>
                <Button
                  type="button"
                  onClick={handleTestWecall}
                  disabled={wecallTesting || !(wecall.webhook_url && wecall.webhook_key)}
                  variant="outline"
                  className="border-brand-gold/30 text-brand-gold hover:bg-brand-gold/10"
                  data-testid="wecall-test-btn"
                >
                  {wecallTesting ? 'Testing…' : 'Send test payload'}
                </Button>
              </div>
            </div>

            {/* Delivery log — instant visibility into WECall AI pushes */}
            <div className="wm-card p-6 rounded-lg" data-testid="wecall-deliveries-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-foreground">Recent lead deliveries</h3>
                <button
                  type="button"
                  onClick={fetchWecallDeliveries}
                  className="text-xs text-muted-foreground hover:text-foreground"
                  data-testid="wecall-refresh-deliveries"
                >
                  Refresh
                </button>
              </div>
              {wecallDeliveries.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No leads pushed yet. When a new lead lands in your CRM, we&apos;ll push it to WECall AI automatically and log the result here.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground uppercase tracking-wider">
                      <tr>
                        <th className="text-left py-2">When</th>
                        <th className="text-left py-2">Lead</th>
                        <th className="text-left py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {wecallDeliveries.map((d) => (
                        <tr key={d.id} className="border-t border-white/5" data-testid={`wecall-delivery-${d.id}`}>
                          <td className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(d.created_at).toLocaleString()}
                          </td>
                          <td className="py-2 text-foreground">
                            <div className="font-medium">{d.lead_name || '—'}</div>
                            <div className="text-xs text-muted-foreground">{d.lead_email || ''}</div>
                          </td>
                          <td className="py-2">
                            {d.ok ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 text-xs">
                                <CheckCircle2 className="w-3 h-3" /> {d.status} OK
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-500/15 text-red-300 text-xs">
                                <AlertTriangle className="w-3 h-3" /> {d.status || 'ERR'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </form>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <div className="space-y-6">
            <div className="wm-card p-6 rounded-lg">
              <h2 className="text-lg font-semibold text-foreground mb-4">Billing & Subscription</h2>
              
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-4 bg-muted rounded-lg">
                  <div>
                    <p className="font-medium text-foreground">Current Plan</p>
                    <p className="text-sm text-muted-foreground capitalize">{user?.membership_tier || 'No active membership'}</p>
                  </div>
                  <Link to="/pricing">
                    <Button variant="outline" className="border-primary text-primary w-full sm:w-auto">
                      {user?.membership_tier && user.membership_tier !== 'none' ? 'Upgrade Plan' : 'View Plans'}
                    </Button>
                  </Link>
                </div>

                <div className="p-4 bg-amber-900/20 border border-amber-500/30 rounded-lg">
                  <p className="text-sm text-amber-400">
                    <strong>Note:</strong> To manage payment methods and view invoices, a Stripe account connection is required. 
                    If you subscribed through Stripe, you can access your billing portal below.
                  </p>
                </div>

                <Button onClick={handleManageBilling} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  <CreditCard className="w-4 h-4 mr-2" /> Manage Payment Methods
                </Button>
              </div>
            </div>

            {/* Invoice History would go here */}
            <div className="wm-card p-6 rounded-lg">
              <h3 className="font-semibold text-foreground mb-4">Payment History</h3>
              <p className="text-muted-foreground text-sm">View your invoices and payment history in the Stripe billing portal above.</p>
            </div>
          </div>
        )}

        {/* Account Tab */}
        {activeTab === 'account' && (
          <div className="space-y-6">
            <div className="wm-card p-6 rounded-lg">
              <h2 className="text-lg font-semibold text-foreground mb-4">Account Settings</h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium text-foreground">{user?.email}</p>
                </div>
                
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Member Since</p>
                  <p className="font-medium text-foreground">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Referral Code</p>
                  <p className="font-medium text-primary font-mono">{user?.referral_code || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Change Password */}
            <ChangePasswordCard />

            {/* Logout */}
            <div className="wm-card p-6 rounded-lg">
              <h3 className="font-semibold text-foreground mb-4">Session</h3>
              <Button onClick={logout} variant="outline" className="w-full border-red-500 text-red-400 hover:bg-red-500/10">
                <LogOut className="w-4 h-4 mr-2" /> Sign Out
              </Button>
            </div>

            {/* Cancel Membership */}
            {user?.membership_tier && user.membership_tier !== 'none' && (
              <div className="wm-card p-6 rounded-lg border border-red-500/30">
                <h3 className="font-semibold text-red-400 mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" /> Cancel Membership
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  If you&apos;re no longer interested in the program, you can cancel your membership. This will:
                </p>
                <ul className="text-sm text-muted-foreground mb-4 space-y-1">
                  <li>• Stop future billing</li>
                  <li>• Revoke access to member features</li>
                  <li>• Keep your profile data for 30 days</li>
                </ul>
                <Button 
                  onClick={() => setShowCancelModal(true)} 
                  variant="outline" 
                  className="border-red-500 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Cancel Membership
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl p-6 max-w-md w-full border border-red-500/30">
            <h3 className="text-xl font-semibold text-foreground mb-4">Cancel Membership?</h3>
            <p className="text-muted-foreground mb-6">
              Are you sure you want to cancel your {user?.membership_tier} membership? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button 
                onClick={() => setShowCancelModal(false)} 
                variant="outline" 
                className="flex-1"
              >
                Keep Membership
              </Button>
              <Button 
                onClick={handleCancelMembership} 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Yes, Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
